import { firstValueFrom, take } from 'rxjs';
import { utils } from '@app/shared';
import {
  AiResultAction,
  AiResultContext,
} from 'src/app/features/ai/components/ai-runner/ai-runner.model';
import {
  AiResultFileFormat,
  aiResultToFile,
} from 'src/app/features/ai/core/utils/ai-result-file.util';
import { TicketDetail } from '../models/ticket.model';
import { TicketService } from '../services/ticket.service';

/**
 * Ações sobre o resultado da IA que gravam no chamado (chamado 0000579137). São fábricas de
 * `AiResultAction`: o `ai-runner-dialog` não conhece chamado, só chama `run`.
 * Toda chamada HTTP passa por `take(1)` (o AuthInterceptor nunca completa; ver CLAUDE.md).
 */
export interface TicketAiActionDeps {
  ticketService: TicketService;
  /** Chamado atual (lido no momento de gravar, não na criação da config). */
  ticket: () => TicketDetail | null;
  /** Avisado depois de cada gravação, para a tela recarregar o chamado. */
  onSaved?: (updated: TicketDetail | null) => void;
}

/** Número do chamado para exibir; chamado sem número do Teorema cai no `ref`, e sem nenhum, vazio. */
export function ticketNumber(
  ticket: Pick<TicketDetail, 'ticket_number_teorema' | 'ref'> | null
): string {
  return String(ticket?.ticket_number_teorema || ticket?.ref || '');
}

function requireTicket(deps: TicketAiActionDeps): TicketDetail {
  const ticket = deps.ticket();
  if (!ticket?.id) throw new Error('Chamado não carregado.');
  return ticket;
}

function variantTitle(ctx: AiResultContext): string {
  return ctx.variant?.label ?? 'Análise';
}

/** Cabeçalho que identifica o texto como gerado pela IA. */
export function aiCommentHeader(ctx: AiResultContext): string {
  return `**🤖 Análise por IA — ${variantTitle(ctx)}**`;
}

/**
 * Adiciona uma tag ao chamado. Tags com o mesmo `replacePrefix` são trocadas (ex.: uma nova
 * `score 7/10` substitui a `score 5/10` anterior, em vez de acumular).
 */
export function ticketTagAction(
  deps: TicketAiActionDeps,
  options: {
    key?: string;
    label?: string;
    tag: (ctx: AiResultContext) => string | null;
    replacePrefix?: string;
  }
): AiResultAction {
  return {
    key: options.key ?? 'ticket-tag',
    label: options.label ?? 'Adicionar tag no chamado',
    icon: 'pi pi-tag',
    available: ctx => !!options.tag(ctx),
    preview: ctx => options.tag(ctx) ?? '',
    run: async ctx => {
      const ticket = requireTicket(deps);
      const tag = options.tag(ctx)?.trim();
      if (!tag) throw new Error('O resultado não trouxe o valor da tag.');
      const prefix = options.replacePrefix?.toLowerCase();
      const names = (ticket.ticket_tags ?? [])
        .map(tt => tt.tag?.name)
        .filter((name): name is string => !!name)
        .filter(name => !prefix || !name.toLowerCase().startsWith(prefix))
        .filter(name => name.toLowerCase() !== tag.toLowerCase());
      const updated = await firstValueFrom(
        deps.ticketService.patchTicket(ticket.id, { ticket_tags: [...names, tag] }).pipe(take(1))
      );
      deps.onSaved?.(updated ?? null);
    },
  };
}

/** Grava o resultado (ou o texto de `text`) como comentário. Privado por padrão. */
export function ticketCommentAction(
  deps: TicketAiActionDeps,
  options: {
    key?: string;
    label?: string;
    text?: (ctx: AiResultContext) => string | null;
    pub?: boolean;
  } = {}
): AiResultAction {
  const body = (ctx: AiResultContext) => (options.text ? options.text(ctx) : ctx.output);
  return {
    key: options.key ?? 'ticket-comment',
    label:
      options.label ??
      (options.pub ? 'Salvar como comentário público' : 'Salvar como comentário privado'),
    icon: 'pi pi-comment',
    available: ctx => !!body(ctx)?.trim(),
    preview: ctx => {
      const text = body(ctx) ?? '';
      return text.length > 400 ? `${text.slice(0, 400)}…` : text;
    },
    // Editável no modal: o texto editado chega em `edited`.
    content: ctx => body(ctx),
    run: async (ctx, edited) => {
      const ticket = requireTicket(deps);
      const text = (edited ?? body(ctx))?.trim();
      if (!text) throw new Error('Resultado vazio.');
      const user = utils.getUser();
      if (!user) throw new Error('Usuário não identificado.');
      const userId = utils.getBwUserCompanyGroup(user)?.user_id || user.id;
      const updated = await firstValueFrom(
        deps.ticketService
          .addComment(ticket.id, {
            observation: `${aiCommentHeader(ctx)}\n\n${text}`,
            user_id: userId,
            user: { name: user.name ?? '' },
            pub: !!options.pub,
          })
          .pipe(take(1))
      );
      deps.onSaved?.(updated ?? null);
    },
  };
}

/** Grava o resultado como arquivo anexo do chamado (md, txt, json ou pdf). */
export function ticketAttachmentAction(
  deps: TicketAiActionDeps,
  options: {
    key?: string;
    label?: string;
    format?: AiResultFileFormat;
    fileName?: (ctx: AiResultContext) => string;
    content?: (ctx: AiResultContext) => unknown;
  } = {}
): AiResultAction {
  const format = options.format ?? 'md';
  const name = (ctx: AiResultContext) => {
    const ticket = deps.ticket();
    return options.fileName
      ? options.fileName(ctx)
      : `ia-${ctx.variant?.pipeline ?? ctx.variant?.key ?? 'analise'}-${ticketNumber(ticket ?? null)}`;
  };
  return {
    key: options.key ?? `ticket-attachment-${format}`,
    label: options.label ?? `Salvar como anexo (.${format})`,
    icon: format === 'pdf' ? 'pi pi-file-pdf' : 'pi pi-paperclip',
    available: ctx => !!ctx.output.trim(),
    preview: ctx => `${name(ctx)}.${format}`,
    // Só o markdown/texto do resultado é editável (conteúdo próprio e json/pdf ficam como estão).
    content: format === 'md' && !options.content ? ctx => ctx.output : undefined,
    run: async (ctx, edited) => {
      const ticket = requireTicket(deps);
      const title = `Chamado${ticketNumber(ticket) ? ` #${ticketNumber(ticket)}` : ''} — ${variantTitle(ctx)}`;
      const content =
        edited !== undefined && format === 'md' && !options.content
          ? edited
          : options.content
            ? options.content(ctx)
            : format === 'json'
              ? (ctx.json ?? ctx.output)
              : ctx.output;
      const file = await aiResultToFile(content, name(ctx), format, title);
      await firstValueFrom(
        deps.ticketService
          .uploadAttachment(ticket.id, file, `chamados/${ticketNumber(ticket) || ticket.id}`)
          .pipe(take(1))
      );
      deps.onSaved?.(null);
    },
  };
}
